import { AlertTriangle, CheckCircle2, Circle, MinusCircle, XCircle } from "lucide-react";
import { ClassificationBadge } from "@/components/ci/classification-badge";
import { Card, CardContent } from "@/components/ui/card";
import { deriveStageFlow, type FlowStage, type FlowStageStatus } from "@/lib/stage-flow";
import type { CiTestResult } from "@/lib/types";
import { cn, formatMs } from "@/lib/utils";

function StageIcon({ status }: { status: FlowStageStatus }) {
  switch (status) {
    case "passed":
      return <CheckCircle2 className="size-4 shrink-0 text-emerald-500" />;
    case "failed":
      return <XCircle className="size-4 shrink-0 text-rose-500" />;
    case "warning":
      return <AlertTriangle className="size-4 shrink-0 text-amber-500" />;
    case "skipped":
      return <MinusCircle className="size-4 shrink-0 text-muted-foreground" />;
    default:
      // not_reached / unknown
      return <Circle className="size-4 shrink-0 text-muted-foreground/50" />;
  }
}

function stageLabelClass(status: FlowStageStatus): string {
  switch (status) {
    case "failed":
      return "text-rose-400";
    case "warning":
      return "text-amber-400";
    case "not_reached":
    case "skipped":
    case "unknown":
      return "text-muted-foreground";
    default:
      return "text-foreground";
  }
}

function StageNode({ stage }: { stage: FlowStage }) {
  return (
    <div className="flex items-center gap-1.5" title={stage.detail ?? undefined}>
      <StageIcon status={stage.status} />
      <span className={cn("text-sm font-medium", stageLabelClass(stage.status))}>
        {stage.label}
      </span>
      {stage.durationMs != null && (
        <span className="font-mono text-xs text-muted-foreground">
          {formatMs(stage.durationMs)}
        </span>
      )}
    </div>
  );
}

function SubstepChips({ stage }: { stage: FlowStage }) {
  if (stage.substeps.length === 0) return null;
  return (
    <span className="flex flex-wrap items-center gap-1">
      <span className="text-muted-foreground">
        {stage.substeps[0]?.kind === "node" ? "nodes:" : "pods:"}
      </span>
      {stage.substeps.map((substep) => (
        <span
          key={substep.label}
          title={substep.error ?? undefined}
          className={cn(
            "rounded border px-1 font-mono text-[10px]",
            substep.status === "failed"
              ? "border-rose-500/30 bg-rose-500/10 text-rose-400"
              : "border-emerald-500/20 bg-emerald-500/5 text-emerald-500",
          )}
        >
          {substep.status === "failed" ? "✗" : "✓"} {substep.label}
        </span>
      ))}
    </span>
  );
}

function StageDetailRow({ stage }: { stage: FlowStage }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      <span className="font-medium text-muted-foreground">{stage.label}:</span>
      {stage.classification && <ClassificationBadge classification={stage.classification} />}
      {stage.error ? (
        <span className="line-clamp-2 break-all font-mono text-rose-400">{stage.error}</span>
      ) : (
        stage.detail && <span className="text-amber-400">{stage.detail}</span>
      )}
      <SubstepChips stage={stage} />
    </div>
  );
}

// Structured "what happened" pipeline shown at the top of the test detail
// page for both passing and failing tests. Hidden when no stage information
// can be derived (e.g. legacy rows with no timings and no failure_phase).
export function CiStageFlow({ result }: { result: CiTestResult }) {
  const stages = deriveStageFlow(result);
  if (stages.length === 0) return null;

  const detailStages = stages.filter(
    (stage) =>
      stage.status === "failed" ||
      (stage.status === "warning" && (stage.detail || stage.substeps.length > 0)) ||
      // A passed vanilla-start probe carries the attribution verdict in its
      // detail — always worth a row, unlike other passed stages.
      (stage.key === "vanilla_start" && stage.detail != null),
  );
  // Failed test whose failure couldn't be attributed to a stage (old rows
  // with failure_phase null) — surface the error at flow level instead.
  const showUnattributedError =
    result.status === "fail" && !stages.some((stage) => stage.status === "failed");

  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <div className="flex flex-wrap items-center gap-y-2">
          {stages.map((stage, index) => (
            <div key={`${stage.key}-${index}`} className="flex items-center">
              {index > 0 && <div className="mx-2.5 h-px w-6 bg-border" />}
              <StageNode stage={stage} />
            </div>
          ))}
        </div>
        {detailStages.map((stage, index) => (
          <StageDetailRow key={`${stage.key}-detail-${index}`} stage={stage} />
        ))}
        {showUnattributedError && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            {result.error_classification && (
              <ClassificationBadge classification={result.error_classification} />
            )}
            <span className="line-clamp-2 break-all font-mono text-rose-400">
              {result.error_classification_detail ?? result.error ?? "unknown failure"}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
